import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Users,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  FileText,
  Edit3,
  Send,
  Trash2,
  Building2,
  ShieldAlert,
  RotateCw,
  X,
  Check,
  Eye,
  UserCheck,
  ArrowRightLeft,
  Info,
  UserPlus,
  ShieldCheck,
  History,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isAdmin, canEditOrDeleteTask, canReassignTask } from "../../lib/permissions";
import AssignTaskModal from "./AssignTaskModal";
import RecurringTaskHistoryModal from "./RecurringTaskHistoryModal";
import ReassignTaskModal from "./ReassignTaskModal";

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed", "Overdue"];

function formatDetailedDate(dateStr, timeStr) {
  if (!dateStr) return "—";
  let formatted = dateStr;
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      formatted = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    }
  } catch {}

  if (timeStr && timeStr.trim()) {
    return `${formatted} at ${timeStr.trim()}`;
  }
  return formatted;
}

function formatReportTimestamp(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      const dateFormatted = d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      const timeFormatted = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return `${dateFormatted} at ${timeFormatted}`;
    }
    return dateStr;
  } catch {
    return dateStr;
  }
}

export default function TaskDetail({ taskId, user, onBack, onChange, onSwitchTask }) {
  const isSuperOrAdmin = isAdmin(user);

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  // Daily report submit state
  const [reportText, setReportText] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);

  // Edit existing report state
  const [editingReportId, setEditingReportId] = useState(null);
  const [editReportContent, setEditReportContent] = useState("");
  const [updatingReport, setUpdatingReport] = useState(false);

  // Status updating state
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Edit / Delete / History / Reassign modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [reassignModalOpen, setReassignModalOpen] = useState(false);

  const loadTask = useCallback(async () => {
    setLoading(true);
    setAuthError("");
    try {
      const res = await api("get", `/tasks/${taskId}`);
      setTask(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setAuthError("You are not authorized to view this task. Tasks are restricted to the assigned team member and administrators.");
      } else if (err.response?.status === 404) {
        setAuthError("Task not found or has been deleted.");
      } else {
        setAuthError(err.response?.data?.detail || "Could not load task details.");
      }
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadTask();
  }, [loadTask]);

  // Handle status update
  const handleStatusChange = async (newStatus) => {
    if (!task || task.status === newStatus || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const res = await api("patch", `/tasks/${task.task_id}/status`, { status: newStatus });
      setTask((prev) => ({ ...prev, ...res.data }));
      toast.success(`Task status marked as ${newStatus}`);
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Submit daily report
  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportText.trim()) {
      toast.error("Please enter report details before saving.");
      return;
    }

    setSubmittingReport(true);
    try {
      await api("post", `/tasks/${task.task_id}/reports`, {
        report: reportText.trim(),
      });
      toast.success("Daily progress report submitted successfully!");
      setReportText("");
      loadTask();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit progress report");
    } finally {
      setSubmittingReport(false);
    }
  };

  // Edit existing report
  const handleSaveEditedReport = async (reportId) => {
    if (!editReportContent.trim()) return;
    setUpdatingReport(true);
    try {
      await api("put", `/tasks/${task.task_id}/reports/${reportId}`, {
        report: editReportContent.trim(),
      });
      toast.success("Report updated successfully");
      setEditingReportId(null);
      setEditReportContent("");
      loadTask();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update report");
    } finally {
      setUpdatingReport(false);
    }
  };

  // Delete task
  const handleDeleteTask = async () => {
    setDeleteBusy(true);
    try {
      await api("delete", `/tasks/${task.task_id}`);
      toast.success("Task deleted successfully");
      setDeleteModalOpen(false);
      if (onChange) onChange();
      if (onBack) onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete task");
    } finally {
      setDeleteBusy(false);
    }
  };

  // Determine whether current user can remove a specific assignee
  const canRemoveAssignee = (assignee) => {
    if (!user || !task || !assignee) return false;
    const assigneesList = task.assignees || [];
    if (assigneesList.length <= 1) return false;
    const isTargetOriginal = assignee.is_original;
    if (isTargetOriginal && !isSuperOrAdmin) return false;
    if (isSuperOrAdmin) return true;
    const uid = user.user_id;
    if (!uid) return false;
    if (assignee.user_id === uid) return true;
    if (task.assigned_by_id === uid || task.created_by_id === uid) return true;
    if (assignee.assigned_by_id === uid) return true;
    const allIds = task.assigned_to_ids || [];
    if (allIds.includes(uid)) return true;
    return false;
  };

  // Handle removing an additional assignee from task
  const handleRemoveAssignee = async (assignee) => {
    if (!assignee || !assignee.user_id) return;
    const confirmName = assignee.name || "this user";
    if (!window.confirm(`Are you sure you want to remove ${confirmName} from this task?`)) {
      return;
    }
    try {
      await api("delete", `/tasks/${task.task_id}/assignees/${assignee.user_id}`);
      toast.success(`Removed ${confirmName} from task`);
      loadTask();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove assignee");
    }
  };

  const getPriorityBadgeClass = (p) => {
    const pr = (p || "Normal").toLowerCase();
    if (pr === "urgent") return "badge red";
    if (pr === "high") return "badge amber";
    if (pr === "low") return "badge slate";
    return "badge blue";
  };

  const getStatusBadgeClass = (s) => {
    const st = (s || "Pending").toLowerCase();
    if (st === "completed") return "badge green";
    if (st === "in progress") return "badge blue";
    if (st === "overdue") return "badge red";
    return "badge amber";
  };

  if (loading) {
    return (
      <div className="content" style={{ padding: "40px 20px", textAlign: "center" }}>
        <p className="muted">Loading task details...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="content" style={{ maxWidth: 800 }}>
        <button className="back-btn" onClick={onBack} data-testid="task-back-btn">
          <ArrowLeft size={14} /> Back to Tasks
        </button>
        <div
          className="panel"
          style={{
            padding: "36px 32px",
            textAlign: "center",
            marginTop: 20,
            borderLeft: "4px solid var(--red)",
          }}
          data-testid="task-auth-error-card"
        >
          <ShieldAlert size={36} color="var(--red)" style={{ margin: "0 auto 12px" }} />
          <h2 style={{ fontSize: 18, color: "var(--navy)", marginBottom: 8 }}>Access Restricted</h2>
          <p style={{ color: "#475569", fontSize: 13.5, maxWidth: 500, margin: "0 auto 20px", lineHeight: 1.6 }}>
            {authError}
          </p>
          <button className="primary-btn" onClick={onBack}>
            Return to Task List
          </button>
        </div>
      </div>
    );
  }

  if (!task) return null;

  const isOverdue = task.status === "Overdue" || task.is_overdue;

  return (
    <div className="content detail-content" data-testid="task-detail-page">
      {/* Back Button */}
      <button className="back-btn" onClick={onBack} data-testid="task-back-btn" style={{ marginBottom: 12 }}>
        <ArrowLeft size={14} /> Back to Tasks & Reports
      </button>

      {/* Overdue Warning Alert if applicable */}
      {isOverdue && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: "#991b1b",
          }}
          data-testid="task-overdue-alert"
        >
          <AlertTriangle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>Task Overdue</div>
            <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 2 }}>
              This task was expected by <b>{formatDetailedDate(task.due_date, task.due_time)}</b>. You can update the status below and submit your progress report.
            </div>
          </div>
        </div>
      )}

      {/* Recurring Occurrence Banner if applicable */}
      {task.is_recurring_instance && task.recurring_task_id && (
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
          }}
          data-testid="task-recurring-banner"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <RotateCw size={18} color="#2563eb" style={{ flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#1e40af" }}>
                Recurring Task Occurrence · {task.recurrence_pattern || "Scheduled Occurrence"}
              </div>
              <div style={{ fontSize: 12, color: "#1d4ed8", marginTop: 2 }}>
                This is an automated occurrence of <b>{task.title}</b>. Its status, daily progress reports, and actions are managed independently.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="outline-btn compact"
            onClick={() => setHistoryModalOpen(true)}
            style={{ fontSize: 11.5, height: 28, background: "#ffffff", borderColor: "#93c5fd", color: "#1d4ed8" }}
            data-testid="task-view-recurring-history-btn"
          >
            <RotateCw size={11} /> View Recurring Schedule & History
          </button>
        </div>
      )}

      {/* Header */}
      <div className="detail-head" style={{ marginBottom: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span className="small-label" style={{ color: "#64748b" }}>
              TASK · {task.task_id}
            </span>
            {task.is_recurring_instance && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "#eff6ff",
                  color: "#1d4ed8",
                  border: "1px solid #bfdbfe",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                }}
                data-testid="task-recurring-badge"
              >
                <RotateCw size={10} /> Recurring
              </span>
            )}
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 4,
                background: "#f1f5f9",
                color: "#334155",
                border: "1px solid #cbd5e1",
              }}
              data-testid="task-detail-company-badge"
            >
              {task.company_name || "Pinaki Solutions"}
            </span>
            <span className={getPriorityBadgeClass(task.priority)} style={{ fontSize: 10 }}>
              {task.priority || "Normal"} Priority
            </span>
            {task.is_self_assigned && (
              <span className="badge blue" style={{ fontSize: 10 }}>
                Self-Assigned
              </span>
            )}
            {task.assignees && task.assignees.length > 1 && (
              <span className="badge amber" style={{ fontSize: 10 }}>
                Multi-User ({task.assignees.length} Assignees)
              </span>
            )}
          </div>
          <h1 style={{ margin: "4px 0 6px", fontSize: 24 }} data-testid="task-detail-title">
            {task.title}
          </h1>
          <p className="muted" style={{ fontSize: 12.5 }}>
            Assigned by{" "}
            <b style={{ color: "var(--navy)" }}>
              {task.is_self_assigned ? `${task.assigned_to_name} (Self)` : task.assigned_by_name || "Admin"}
            </b>{" "}
            on {formatDetailedDate(task.assigned_at || task.created_at)}
          </p>
        </div>

        {/* Action buttons (Assign to Another User, Edit Task & Delete) */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {canReassignTask(user, task) && (
            <button
              className="outline-btn"
              onClick={() => setReassignModalOpen(true)}
              data-testid="task-reassign-btn"
              title="Assign this task to another eligible team member from your organisation"
            >
              <UserCheck size={14} /> Assign to Another User
            </button>
          )}
          {canEditOrDeleteTask(user, task) && (
            <>
              <button
                className="outline-btn"
                onClick={() => setEditModalOpen(true)}
                data-testid="task-edit-btn"
              >
                <Edit3 size={14} /> Edit Task
              </button>
              <button
                className="outline-btn danger"
                onClick={() => setDeleteModalOpen(true)}
                data-testid="task-delete-btn"
              >
                <Trash2 size={14} /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Current Status Control Card */}
      <div
        className="panel"
        style={{
          padding: "12px 18px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
        data-testid="task-status-bar"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b" }}>Current Status:</span>
          <span className={getStatusBadgeClass(task.status)} style={{ fontSize: 12, padding: "5px 12px" }}>
            {task.status}
          </span>
          {task.status === "Completed" && task.completed_at && (
            <span style={{ fontSize: 12, color: "var(--green)", fontWeight: 500 }}>
              ✓ Completed on {formatDetailedDate(task.completed_at)}
            </span>
          )}
        </div>

        {/* Status quick switcher */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11.5, color: "#64748b", marginRight: 4 }}>Change Status:</span>
          {STATUS_OPTIONS.map((st) => (
            <button
              key={st}
              className={`chip ${task.status === st ? "active" : ""}`}
              onClick={() => handleStatusChange(st)}
              disabled={updatingStatus}
              data-testid={`status-toggle-${st.toLowerCase().replace(" ", "-")}`}
            >
              {task.status === st && <Check size={12} style={{ marginRight: 4 }} />}
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* Multi-User Group Overview Panel (For Admins) */}
      {isSuperOrAdmin && task.group_members && task.group_members.length > 0 && (
        <div
          className="panel"
          style={{ marginBottom: 20, border: "1px solid #bfdbfe", background: "#f8fafc" }}
          data-testid="task-multi-user-overview"
        >
          <div className="panel-title" style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Users size={16} color="#2563eb" />
              <h3 style={{ margin: 0, fontSize: 15, color: "var(--navy)" }}>
                Multi-User Team Progress ({task.group_members.length} Assignees)
              </h3>
            </div>
            <small className="muted">Each assigned user tracks their own status and reports independently.</small>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            {task.group_members.map((member) => {
              const isCurrent = member.task_id === task.task_id;
              const lastRep = member.last_report;
              return (
                <div
                  key={member.task_id}
                  style={{
                    background: isCurrent ? "#eff6ff" : "#ffffff",
                    border: isCurrent ? "2px solid #3b82f6" : "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "10px 14px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <div>
                      <b style={{ fontSize: 13, color: "var(--navy)" }}>
                        {member.assigned_to_name || "User"} {isCurrent && "(Viewing)"}
                      </b>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{member.department || "Sales"}</div>
                    </div>
                    <span className={getStatusBadgeClass(member.status)} style={{ fontSize: 10.5 }}>
                      {member.status}
                    </span>
                  </div>

                  <div style={{ fontSize: 12, color: "#334155", background: "#f8fafc", padding: "6px 8px", borderRadius: 4, minHeight: 36, display: "flex", alignItems: "center" }}>
                    {lastRep ? (
                      <span title={lastRep.report}>
                        {lastRep.report.length > 55 ? lastRep.report.slice(0, 55) + "..." : lastRep.report}
                      </span>
                    ) : (
                      <span style={{ color: "#94a3b8", fontStyle: "italic" }}>No report submitted</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Section: Balanced Two-Column Grid for Task Information & Submit Progress Report */}
      <div
        className="detail-grid"
        style={{
          gridTemplateColumns: "1.15fr 1fr",
          gap: 20,
          marginBottom: 20,
          alignItems: "start",
        }}
      >
        {/* Left Column: Task Information Card */}
        <div className="panel" data-testid="task-info-card" style={{ display: "flex", flexDirection: "column" }}>
          <div className="panel-title" style={{ marginBottom: 16 }}>
            <h3>Task Information</h3>
          </div>

          <div className="facts" style={{ gridTemplateColumns: "repeat(2, 1fr)", rowGap: 16 }}>
            <div>
              <small>Company / Organization</small>
              <b style={{ display: "block", fontSize: 13.5, color: "var(--navy)" }} data-testid="task-detail-company">
                {task.company_name || "Pinaki Solutions"}
              </b>
            </div>

            <div>
              <small>Department</small>
              <b style={{ fontSize: 13.5 }}>{task.department || "General"}</b>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <small style={{ margin: 0 }}>
                  Assigned Team Members ({task.assignees?.length || 1})
                </small>
                {canReassignTask(user, task) && (
                  <button
                    type="button"
                    className="outline-btn compact"
                    onClick={() => setReassignModalOpen(true)}
                    style={{ fontSize: 11, padding: "2px 8px", height: 24, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}
                    data-testid="inline-reassign-btn"
                    title="Assign to another user without removing current assignees"
                  >
                    <UserPlus size={11} /> Assign to Another User
                  </button>
                )}
              </div>

              {(() => {
                const assigneesList = Array.isArray(task.assignees) && task.assignees.length > 0
                  ? task.assignees
                  : [
                      {
                        user_id: task.original_assigned_to_id || task.assigned_to_id,
                        name: task.original_assigned_to_name || task.assigned_to_name || "Assignee",
                        email: task.assigned_to_email || "",
                        department: task.department || "General",
                        is_original: true,
                      },
                    ];

                const originalAssignee =
                  assigneesList.find((a) => a.is_original) ||
                  assigneesList[0];

                const additionalAssignees = assigneesList.filter((a) => a !== originalAssignee && !a.is_original);

                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {/* Original Assignee Card */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        gap: 10,
                      }}
                      data-testid="original-assignee-card"
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "var(--navy)",
                            color: "#ffffff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          {(originalAssignee.name || "U").trim().charAt(0).toUpperCase()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <b style={{ fontSize: 13, color: "var(--navy)" }}>
                              {originalAssignee.name || task.assigned_to_name || "Assignee"}
                            </b>
                            {task.is_self_assigned && (
                              <span style={{ fontSize: 10.5, color: "#2563eb" }}>(Self)</span>
                            )}
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 700,
                                padding: "2px 6px",
                                borderRadius: 4,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                border: "1px solid #bfdbfe",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 3,
                              }}
                            >
                              <ShieldCheck size={10} /> Original Assignee
                            </span>
                          </div>
                          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 1 }}>
                            {originalAssignee.email || task.assigned_to_email || "No email"} · {originalAssignee.department || task.department || "General"}
                          </div>
                        </div>
                      </div>

                      {canRemoveAssignee(originalAssignee) && (
                        <button
                          type="button"
                          className="icon-btn danger"
                          onClick={() => handleRemoveAssignee(originalAssignee)}
                          title="Remove Assignee"
                          style={{ padding: 4 }}
                          data-testid={`remove-assignee-${originalAssignee.user_id}`}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>

                    {/* Additional Assignees */}
                    {additionalAssignees.map((assignee) => (
                      <div
                        key={assignee.user_id}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          padding: "8px 12px",
                          background: "#ffffff",
                          border: "1px solid #cbd5e1",
                          borderRadius: 8,
                          gap: 10,
                        }}
                        data-testid={`additional-assignee-${assignee.user_id}`}
                      >
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: "#3b82f6",
                              color: "#ffffff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 700,
                              fontSize: 12,
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          >
                            {(assignee.name || "U").trim().charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <b style={{ fontSize: 13, color: "var(--navy)" }}>{assignee.name || "User"}</b>
                              <span className="badge amber" style={{ fontSize: 10 }}>Co-Assignee</span>
                              <span className="badge slate" style={{ fontSize: 10 }}>
                                {assignee.department || "General"}
                              </span>
                            </div>
                            <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                              {assignee.email && <span>{assignee.email} · </span>}
                              <span>Added by <b>{assignee.assigned_by_name || "Team Member"}</b> on {formatDetailedDate(assignee.assigned_at)}</span>
                            </div>
                            {assignee.note && (
                              <div style={{ fontSize: 11.5, color: "#475569", marginTop: 3, fontStyle: "italic", background: "#f8fafc", padding: "3px 8px", borderRadius: 4 }}>
                                Note: "{assignee.note}"
                              </div>
                            )}
                          </div>
                        </div>

                        {canRemoveAssignee(assignee) && (
                          <button
                            type="button"
                            className="icon-btn danger"
                            onClick={() => handleRemoveAssignee(assignee)}
                            title="Remove Assignee"
                            style={{ padding: 4 }}
                            data-testid={`remove-assignee-${assignee.user_id}`}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            <div>
              <small>Assigned By</small>
              <b style={{ display: "block", fontSize: 13.5 }}>
                {task.is_self_assigned ? `${task.assigned_to_name} (Self)` : task.assigned_by_name || "Administrator"}
              </b>
              <span style={{ fontSize: 11.5, color: "#64748b" }}>
                {formatDetailedDate(task.assigned_at || task.created_at)}
              </span>
            </div>

            <div>
              <small>Target Due Date & Time</small>
              <b style={{ fontSize: 13.5, color: isOverdue ? "#dc2626" : "var(--navy)" }}>
                {formatDetailedDate(task.due_date, task.due_time)}
              </b>
            </div>

            {task.start_time && (
              <div>
                <small>Start Time</small>
                <b style={{ fontSize: 13.5 }}>{task.start_time}</b>
              </div>
            )}

            <div>
              <small>Priority Level</small>
              <span className={getPriorityBadgeClass(task.priority)} style={{ marginTop: 3 }}>
                {task.priority || "Normal"}
              </span>
            </div>

            <div>
              <small>Completion Status</small>
              <b style={{ fontSize: 13.5 }}>
                {task.status === "Completed"
                  ? `Completed (${formatDetailedDate(task.completed_at)})`
                  : task.status}
              </b>
            </div>

            {/* Assignment Trail & History */}
            {((Array.isArray(task.assignment_history) && task.assignment_history.length > 0) || task.reassigned_at) && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: "12px 14px",
                  marginTop: 6,
                }}
                data-testid="task-assignment-audit-card"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>
                  <History size={13} color="#2563eb" />
                  <span>Assignment Trail & History</span>
                </div>

                {Array.isArray(task.assignment_history) && task.assignment_history.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {task.assignment_history.map((hist, idx) => (
                      <div
                        key={hist.history_id || idx}
                        style={{
                          fontSize: 12,
                          color: "#334155",
                          padding: "6px 10px",
                          background: "#ffffff",
                          border: "1px solid #f1f5f9",
                          borderRadius: 6,
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {hist.action === "added_assignee" ? (
                            <span style={{ color: "#16a34a", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                              <UserPlus size={12} /> Added Assignee:
                            </span>
                          ) : (
                            <span style={{ color: "#dc2626", display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}>
                              <X size={12} /> Removed Assignee:
                            </span>
                          )}
                          <b>{hist.user_name || "User"}</b>
                          <span style={{ color: "#64748b" }}>
                            by <b>{hist.assigned_by_name || hist.removed_by_name || "User"}</b>
                          </span>
                          <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: "auto" }}>
                            {formatReportTimestamp(hist.timestamp)}
                          </span>
                        </div>
                        {hist.note && (
                          <div style={{ fontSize: 11.5, color: "#475569", fontStyle: "italic", paddingLeft: 18 }}>
                            "{hist.note}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#1e293b" }}>
                      <UserCheck size={14} color="#2563eb" />
                      <span>
                        Reassigned by <b style={{ color: "var(--navy)" }}>{task.reassigned_by_name}</b> on {formatDetailedDate(task.reassigned_at)}
                      </span>
                      {task.previous_assigned_to_name && (
                        <span style={{ color: "#64748b", fontWeight: 400 }}>
                          · Previously: {task.previous_assigned_to_name}
                        </span>
                      )}
                    </div>
                    {task.latest_reassignment_reason && (
                      <div style={{ fontSize: 12, color: "#475569", marginTop: 4, fontStyle: "italic", paddingLeft: 20 }}>
                        "{task.latest_reassignment_reason}"
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div style={{ marginTop: "auto", paddingTop: 18, borderTop: "1px solid #f1f5f9" }}>
            <div className="small-label" style={{ marginBottom: 6 }}>
              TASK DESCRIPTION
            </div>
            <p
              style={{
                fontSize: 13.5,
                color: "#1e293b",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                margin: 0,
              }}
              data-testid="task-description-text"
            >
              {task.description || "No specific instructions provided."}
            </p>
          </div>
        </div>

        {/* Right Column: Submit Progress Report Card */}
        <div
          className="panel"
          data-testid="submit-report-panel"
          style={{ alignSelf: "flex-start" }}
        >
          <div
            className="panel-title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FileText size={17} color="var(--navy)" />
              <h3 style={{ margin: 0, fontSize: 16, color: "var(--navy)", fontWeight: 600 }}>
                Submit Progress Report
              </h3>
            </div>
            <span className="badge blue" style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px" }}>
              Daily Update
            </span>
          </div>

          <p style={{ fontSize: 12.5, color: "#64748b", margin: "0 0 14px", lineHeight: 1.45 }}>
            Record daily progress, milestones reached, or key blockers encountered for this task.
          </p>

          <form
            onSubmit={handleSubmitReport}
            data-testid="task-report-form"
          >
            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <label
                  htmlFor="task-report-textarea"
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  Daily Progress Details <span style={{ color: "var(--red)" }}>*</span>
                </label>
                <span
                  style={{
                    fontSize: 11.5,
                    color: reportText.length > 0 ? "var(--navy)" : "#94a3b8",
                    fontWeight: reportText.length > 0 ? 500 : 400,
                  }}
                  data-testid="report-character-counter"
                >
                  {reportText.length} characters
                </span>
              </div>

              <textarea
                id="task-report-textarea"
                className="dispatch-control"
                rows={5}
                placeholder="e.g. Completed initial design draft, conducted client meeting and agreed on timeline. Next step: prepare production files..."
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: 13,
                  lineHeight: 1.5,
                  borderRadius: 8,
                  border: "1.5px solid #cbd5e1",
                  background: "#ffffff",
                  resize: "vertical",
                  minHeight: 110,
                  boxSizing: "border-box",
                  outline: "none",
                }}
                data-testid="task-report-textarea"
              />

              <div
                style={{
                  fontSize: 11.5,
                  color: "#64748b",
                  marginTop: 6,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  lineHeight: 1.4,
                }}
              >
                <Info size={13} color="#64748b" style={{ flexShrink: 0 }} />
                <span>Submitting a progress report automatically transitions Pending tasks to In Progress.</span>
              </div>
            </div>

            <button
              type="submit"
              className="primary-btn wide"
              disabled={submittingReport || !reportText.trim()}
              data-testid="save-report-submit-btn"
              style={{
                width: "100%",
                height: 40,
                fontSize: 13.5,
                fontWeight: 600,
                justifyContent: "center",
                gap: 8,
                background: reportText.trim() ? "var(--navy)" : "#94a3b8",
                borderColor: reportText.trim() ? "var(--navy)" : "#94a3b8",
                cursor: submittingReport || !reportText.trim() ? "not-allowed" : "pointer",
                transition: "all 0.15s ease",
              }}
            >
              <Send size={15} />
              {submittingReport ? "Submitting Report..." : "Submit Daily Report"}
            </button>
          </form>
        </div>
      </div>

      {/* Bottom Section: Full-Width Daily Progress Reports History */}
      <div className="panel" data-testid="task-report-history-card" style={{ marginBottom: 20 }}>
        <div
          className="panel-title"
          style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FileText size={18} color="var(--navy)" />
            <h3 style={{ margin: 0, fontSize: 16, color: "var(--navy)" }}>
              Daily Progress Reports ({task.reports_count || (task.reports || []).length || 0})
            </h3>
          </div>
          <small className="muted">All updates submitted for this task</small>
        </div>

        {(task.reports || []).length === 0 ? (
          <div
            style={{
              padding: "40px 16px",
              textAlign: "center",
              background: "#f8fafc",
              borderRadius: 8,
              border: "1px dashed var(--border)",
            }}
          >
            <Clock size={28} color="#94a3b8" style={{ margin: "0 auto 10px" }} />
            <p style={{ margin: 0, fontSize: 13.5, fontWeight: 500, color: "#475569" }}>
              No daily reports submitted yet.
            </p>
            <p style={{ margin: "6px 0 0", fontSize: 12.5, color: "#94a3b8" }}>
              Use the progress report form above to log daily updates and blockers.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {task.reports.map((rep) => {
              const isEditingThis = editingReportId === rep.report_id;
              const canEditThis = isSuperOrAdmin || rep.user_id === user.user_id;
              return (
                <div
                  key={rep.report_id}
                  style={{
                    padding: "16px 18px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    borderLeft: "4px solid #3b82f6",
                  }}
                  data-testid={`report-item-${rep.report_id}`}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: "#e0e7ff",
                          color: "#4338ca",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {(rep.user_name || "U")[0].toUpperCase()}
                      </div>
                      <b style={{ fontSize: 13.5, color: "var(--navy)" }}>{rep.user_name || "User"}</b>
                      {rep.user_department && (
                        <span className="badge slate" style={{ fontSize: 10 }}>{rep.user_department}</span>
                      )}
                      <span style={{ fontSize: 12, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Clock size={12} color="#94a3b8" /> {formatReportTimestamp(rep.created_at)}
                      </span>
                      {rep.updated_at && rep.updated_at !== rep.created_at && (
                        <span style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                          (edited)
                        </span>
                      )}
                    </div>

                    {canEditThis && !isEditingThis && (
                      <button
                        className="icon-btn"
                        style={{ padding: 4, height: "auto" }}
                        onClick={() => {
                          setEditingReportId(rep.report_id);
                          setEditReportContent(rep.report);
                        }}
                        title="Edit Report"
                        data-testid={`edit-report-btn-${rep.report_id}`}
                      >
                        <Edit3 size={13} />
                      </button>
                    )}
                  </div>

                  {isEditingThis ? (
                    <div>
                      <textarea
                        className="dispatch-control"
                        rows={4}
                        value={editReportContent}
                        onChange={(e) => setEditReportContent(e.target.value)}
                        style={{ width: "100%", padding: "10px 12px", fontSize: 13, borderRadius: 8 }}
                        data-testid={`edit-report-input-${rep.report_id}`}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 10 }}>
                        <button
                          className="outline-btn compact"
                          onClick={() => setEditingReportId(null)}
                          disabled={updatingReport}
                        >
                          Cancel
                        </button>
                        <button
                          className="primary-btn compact"
                          onClick={() => handleSaveEditedReport(rep.report_id)}
                          disabled={updatingReport}
                          data-testid={`save-report-btn-${rep.report_id}`}
                        >
                          {updatingReport ? "Saving..." : "Save Report"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        margin: 0,
                        fontSize: 13.5,
                        color: "#1e293b",
                        lineHeight: 1.65,
                        whiteSpace: "pre-wrap",
                        background: "#ffffff",
                        padding: "10px 14px",
                        borderRadius: 8,
                        border: "1px solid #f1f5f9",
                      }}
                    >
                      {rep.report}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Task Modal */}
      {editModalOpen && (
        <AssignTaskModal
          task={task}
          user={user}
          onClose={(saved) => {
            setEditModalOpen(false);
            if (saved) {
              loadTask();
              if (onChange) onChange();
            }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="modal-backdrop">
          <div
            className="modal"
            style={{ maxWidth: 420 }}
            data-testid="task-delete-modal"
          >
            <div className="modal-head" style={{ marginBottom: 14 }}>
              <div>
                <div className="small-label" style={{ color: "var(--red)" }}>
                  DELETE TASK
                </div>
                <h2 style={{ fontSize: 18, marginTop: 4 }}>Confirm Deletion</h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setDeleteModalOpen(false)}
              >
                <X size={18} />
              </button>
            </div>

            <p style={{ fontSize: 13.5, color: "#334155", margin: "0 0 18px", lineHeight: 1.5 }}>
              Are you sure you want to delete task <b>"{task.title}"</b>? This task will be removed from all active views.
            </p>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                className="outline-btn"
                onClick={() => setDeleteModalOpen(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                style={{ background: "var(--red)", borderColor: "var(--red)" }}
                onClick={handleDeleteTask}
                disabled={deleteBusy}
                data-testid="confirm-task-delete-btn"
              >
                {deleteBusy ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring Task History Modal */}
      {historyModalOpen && task.recurring_task_id && (
        <RecurringTaskHistoryModal
          recurringTaskId={task.recurring_task_id}
          user={user}
          onClose={() => setHistoryModalOpen(false)}
          onOpenTask={(tid) => {
            setHistoryModalOpen(false);
            if (onSwitchTask) onSwitchTask(tid);
          }}
          onEditRecurring={() => {
            setHistoryModalOpen(false);
            setEditModalOpen(true);
          }}
        />
      )}

      {/* Reassign Task Modal */}
      {reassignModalOpen && (
        <ReassignTaskModal
          task={task}
          user={user}
          onClose={() => setReassignModalOpen(false)}
          onReassigned={() => {
            setReassignModalOpen(false);
            loadTask();
            if (onChange) onChange();
          }}
        />
      )}
    </div>
  );
}
